import {Link} from 'react-router-dom'
import {useTheme, BrandTheme} from '@/contexts/theme-context'
import {useI18n} from '@/i18n'
import {brandName} from '@/lib/runtime-config'

interface LogoProps {
    suffix?: string
}

function LumonLogo({className}: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 390.2 51.1" fill="currentColor" className={className}>
            <path d="M0,49.7V1.4c0-0.7,0.5-1.2,1.2-1.2h9.7c0.8,0,1.2,0.4,1.2,1.2V41h50.8c0.8,0,1.2,0.4,1.2,1.2v7.6c0,0.7-0.5,1.2-1.2,1.2H1.2
        C0.5,50.9,0,50.4,0,49.7z M69.4,37.6V1.4c0-0.7,0.5-1.2,1.2-1.2h9.7c0.8,0,1.2,0.4,1.2,1.2v35.4c0,3.1,1.6,4.1,4.5,4.1h36.9
        c3,0,4.5-1,4.5-4.1V1.4c0-0.7,0.4-1.2,1.2-1.2h9.7c0.8,0,1.2,0.4,1.2,1.2v36.2c0,8.2-4.2,13.2-13.8,13.2H83.2
        C73.6,50.9,69.4,45.9,69.4,37.6z M150.1,49.7V1.4c0-0.7,0.5-1.2,1.2-1.2h14.1c0.8,0,1.2,0.4,1.7,1.1l24,36.5h1.1l24-36.5
        c0.5-0.7,0.9-1.1,1.7-1.1H232c0.8,0,1.2,0.4,1.2,1.2v48.3c0,0.7-0.4,1.2-1.2,1.2h-9.7c-0.8,0-1.2-0.4-1.2-1.2V15.3h-0.9l-22.5,34.4
        c-0.5,0.7-1,1.2-1.9,1.2h-8.4c-0.8,0-1.3-0.4-1.8-1.2L163,15.3h-0.9v34.4c0,0.7-0.4,1.2-1.2,1.2h-9.7
        C150.5,50.9,150.1,50.4,150.1,49.7z M312.1,13.1v25c0,8.1-5.2,13.1-13,13.1h-44.2c-7.7,0-13-5-13-13.1v-25c0-8.1,5.3-13.1,13-13.1
        h44.3C306.8,0,312.1,5,312.1,13.1z M277.1,42.1c6.7,0,12.3-5.3,12.3-11.5c0-2.4-0.8-4.6-2.1-6.4l-8.4-13.9c-1.2-2-2.5-2-3.6,0
        l-8.4,13.9c-1.3,1.9-2,4-2,6.4C264.9,36.8,270.3,42.1,277.1,42.1z M321.3,49.7V1.4c0-0.7,0.5-1.2,1.2-1.2h11.3c0.7,0,1,0.3,1.7,0.9
        L377,37.6h1V1.4c0-0.7,0.5-1.2,1.2-1.2h9.7c0.8,0,1.2,0.4,1.2,1.2v48.3c0,0.7-0.5,1.2-1.2,1.2h-11.3c-0.8,0-1.1-0.3-1.8-0.9
        l-41.3-36.4h-1.1v36.1c0,0.7-0.4,1.2-1.2,1.2h-9.7C321.8,50.9,321.3,50.4,321.3,49.7z"/>
        </svg>
    )
}

function SwissCrossLogo({className}: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className={className}>
            {/* eslint-disable-next-line no-restricted-syntax -- Swiss flag brand colors are fixed national colors */}
            <rect fill="#DC0018" width="32" height="32"/>
            {/* eslint-disable-next-line no-restricted-syntax -- Swiss flag brand colors are fixed national colors */}
            <rect fill="#FFFFFF" x="13" y="6" width="6" height="20"/>
            {/* eslint-disable-next-line no-restricted-syntax -- Swiss flag brand colors are fixed national colors */}
            <rect fill="#FFFFFF" x="6" y="13" width="20" height="6"/>
        </svg>
    )
}

function MuenchenstiftSkyline({className}: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 34" fill="currentColor" className={className}>
            {/* Munich skyline silhouette — based on MÜNCHENSTIFT favicon */}
            <path d="M0,34 L0,26 L3,26 L3,22 L5,22 L5,18 L7,18 L7,16 L8,12 L9,16 L10,16 L10,14 L11,8 L12,14 L13,16
                     L13,18 L15,18 L15,20 L17,20 L17,16 L18,16 L18,12 L19,6 L20,12 L21,12 L21,10 L22,2 L23,0 L24,2
                     L25,10 L26,12 L26,14 L27,14 L27,16 L29,16 L29,12 L30,8 L31,12 L32,16 L33,16 L33,18 L35,18
                     L35,20 L37,20 L37,16 L38,16 L38,14 L39,10 L40,14 L41,16 L42,16 L42,20 L44,20 L44,24 L46,24
                     L46,26 L48,26 L48,34 Z"/>
        </svg>
    )
}

function BundesdruckereiShield({className}: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 28" fill="currentColor" className={className}>
            {/* Shield with keyhole — Bundesdruckerei security identity */}
            <path d="M12 0L0 5v8c0 8.4 5.1 16.2 12 18 6.9-1.8 12-9.6 12-18V5L12 0zm0 2.2l10 4.2v6.6c0 7-4.2 13.5-10 15.2C6.2 26.5 2 20 2 13V6.4l10-4.2z" fillOpacity="0.2"/>
            <path d="M12 0L0 5v8c0 8.4 5.1 16.2 12 18 6.9-1.8 12-9.6 12-18V5L12 0zm0 2.2l10 4.2v6.6c0 7-4.2 13.5-10 15.2C6.2 26.5 2 20 2 13V6.4l10-4.2z"/>
            <circle cx="12" cy="11" r="2.5" fillOpacity="0.8"/>
            <path d="M10.8 13h2.4v5.5h-2.4z" fillOpacity="0.8"/>
        </svg>
    )
}

function AargauLogo({className}: { className?: string }) {
    return (
        // eslint-disable-next-line i18next/no-literal-string -- brand logo alt text
        <img src="/brands/aargau-logo.png" alt="Kanton Aargau" className={className}/>
    )
}

function IdPulseLogo({className}: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 10 350 80" className={className}>
            {/* Pulse/heartbeat icon + "idpulse" text — from brand logo SVG */}
            { }
            <path d="M8 55 L22 55 L30 75 L42 20 L54 65 L62 45 L72 55 L88 55" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
            {/* eslint-disable-next-line i18next/no-literal-string -- brand name rendered in SVG */}
            <text x="105" y="72" fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" fontWeight="600" fontSize="68" fill="currentColor" letterSpacing="-1">idpulse</text>
        </svg>
    )
}

function handleLogoFallback(event: React.SyntheticEvent<HTMLImageElement>) {
    const img = event.currentTarget
    // eslint-disable-next-line i18next/no-literal-string -- static asset path, not user-visible text
    img.src = '/brand/logo-light.svg'
    img.onerror = null
}

export function Logo({suffix}: LogoProps) {
    const {brandTheme, resolvedTheme} = useTheme()
    const i18n = useI18n()

    const renderBrandLogo = () => {
        switch (brandTheme) {
            case BrandTheme.LUMON:
                return <LumonLogo className="h-3.5 w-auto text-secondary shrink-0 self-center"/>
            case BrandTheme.SWISS:
                return (
                    <>
                        <SwissCrossLogo className="h-5 w-5 shrink-0 self-center"/>
                        <span
                            className="font-bold text-lg tracking-tight text-primary dark:text-white leading-none">{i18n.common.swissAdmin}</span>
                    </>
                )
            case BrandTheme.MUENCHENSTIFT:
                return (
                    <>
                        <MuenchenstiftSkyline className="h-3.5 w-auto text-primary shrink-0 self-center -translate-y-px"/>
                        {/* eslint-disable-next-line i18next/no-literal-string -- brand name */}
                        <span className="font-extrabold text-sm tracking-[0.15em] text-primary leading-none">MÜNCHENSTIFT</span>
                    </>
                )
            case BrandTheme.BUNDESDRUCKEREI:
                return (
                    <>
                        <BundesdruckereiShield className="h-5 w-auto text-primary shrink-0 self-center"/>
                        {/* eslint-disable-next-line i18next/no-literal-string -- brand name */}
                        <span className="font-bold text-sm tracking-wide text-primary dark:text-white leading-none">Bundesdruckerei</span>
                    </>
                )
            case BrandTheme.AARGAU:
                return <AargauLogo className="h-10 w-auto shrink-0 self-center"/>
            case BrandTheme.DEFAULT:
                return <IdPulseLogo className="h-5 w-auto text-primary shrink-0 self-center"/>
            case BrandTheme.TENANT:
                return (
                    <img
                        src={resolvedTheme === 'dark' ? '/brand/logo-dark.svg' : '/brand/logo-light.svg'}
                        alt={brandName() ?? ''}
                        className="h-5 w-auto shrink-0 self-center"
                        onError={handleLogoFallback}
                    />
                )
        }
    }

    return (
        <Link to="/" className="flex items-center gap-2" data-testid="link-home">
            {renderBrandLogo()}
            {suffix && (
                <>
                    <span className="text-muted-foreground">/</span>
                    <span className="font-medium text-muted-foreground">{suffix}</span>
                </>
            )}
        </Link>
    )
}
